import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { categoryLabel, GROUP_ORDER } from './api'

// THE mobile sport/sportsbook picker — one bottom sheet shared by the embed
// page, distribution/trading, and affiliate. Same taxonomy as the desktop
// sidebars (shared GROUP_ORDER, alphabetical within groups, single-open
// accordion) so the two surfaces can't drift. Items without a category
// render as one flat list (sportsbooks); pinned items render below the
// groups (SPARKET).

export interface SportSheetItem {
  key: string
  label: string
  // Raw catalog category ('Motor Sports', …). Omit on every item for a
  // flat, ungrouped list.
  category?: string
  // Dim text after the label (slug).
  sub?: string
  // Right-edge annotation (live dot, S/I counts, GUIDE).
  right?: ReactNode
  // Render below the category groups instead of inside them.
  pinned?: boolean
}

interface SportSheetProps {
  title: string
  items: SportSheetItem[]
  selectedKey: string | null
  onSelect: (key: string) => void
  onClose: () => void
}

// ── Opener bus ──────────────────────────────────────────────────────
// The header's mobile menu has a SPORTS row but no access to any page's
// sport list; each page app registers its own "open my sheet" handler.
let opener: (() => void) | null = null

export function openSportSheet(): void {
  opener?.()
}

export function useSportSheetOpener(fn: () => void): void {
  useEffect(() => {
    opener = fn
    return () => {
      if (opener === fn) opener = null
    }
  }, [fn])
}

// ── Sticky switcher bar ─────────────────────────────────────────────
// Mobile-only replacement for the squeezed 240px sidebar box on the ad
// pages: shows the current selection, one tap opens the sheet. Sticky so
// it stays reachable however deep the editor scrolls.
export function SportSheetBar({ label, placeholder, onOpen }: {
  label: string | null
  placeholder: string
  onOpen: () => void
}) {
  return (
    <button type="button" className="bm-sportbar" onClick={onOpen}>
      <span className="ad-list-group-caret" aria-hidden="true">▾</span>
      <span className="bm-sportbar-label">{label ?? placeholder}</span>
      <span className="b-mono-dim b-tiny">SWITCH</span>
    </button>
  )
}

// ── The sheet ───────────────────────────────────────────────────────
export function SportSheet({ title, items, selectedKey, onSelect, onClose }: SportSheetProps) {
  const [query, setQuery] = useState('')
  const hasCategories = items.some(i => i.category)

  // Single-open accordion, opening on the selected item's group so the
  // current sport is visible the moment the sheet slides up.
  const [openCat, setOpenCat] = useState<string | null>(() => {
    const sel = items.find(i => i.key === selectedKey)
    if (sel?.category) return categoryLabel(sel.category)
    return hasCategories ? GROUP_ORDER[0] : null
  })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const q = query.trim().toLowerCase()
  const byLabel = (a: SportSheetItem, b: SportSheetItem) => a.label.localeCompare(b.label)

  const { grouped, pinned } = useMemo(() => {
    const matched = q
      ? items.filter(i => i.label.toLowerCase().includes(q) || (i.sub ?? '').toLowerCase().includes(q))
      : items
    const pinned = matched.filter(i => i.pinned)
    const listed = matched.filter(i => !i.pinned)
    const m = new Map<string, SportSheetItem[]>()
    if (!hasCategories) {
      m.set('', [...listed].sort(byLabel))
      return { grouped: m, pinned }
    }
    for (const label of GROUP_ORDER) m.set(label, [])
    for (const it of listed) {
      const k = it.category ? categoryLabel(it.category) : 'OTHER'
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(it)
    }
    for (const [k, v] of m) {
      if (v.length === 0) m.delete(k)
      else v.sort(byLabel)
    }
    return { grouped: m, pinned }
  }, [items, q, hasCategories])

  const searching = q.length > 0

  function renderRow(it: SportSheetItem) {
    const active = it.key === selectedKey
    return (
      <button
        type="button"
        key={it.key}
        onClick={() => onSelect(it.key)}
        className={`ad-list-row${active ? ' is-active' : ''}`}
      >
        <span className="ad-list-name">
          {it.label}
          {it.sub && (
            <span className="b-mono-dim ad-list-slug">
              {' '}
              ·
              {it.sub}
            </span>
          )}
        </span>
        {it.right}
      </button>
    )
  }

  return (
    <div
      className="b-sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="presentation"
    >
      <div className="b-sheet" role="dialog" aria-label={title}>
        <div className="b-sheet-head">
          <span className="b-mono">{title}</span>
          <button type="button" className="b-btn-ghost b-btn-tiny" onClick={onClose}>
            ✕ CLOSE
          </button>
        </div>
        <div className="ad-list-search">
          <span className="b-mono-dim">/</span>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="filter…"
            className="ad-list-search-input"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="ad-list-search-clear"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <div className="b-sheet-body">
          {grouped.size === 0 && pinned.length === 0
            ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
            : [...grouped.entries()].map(([cat, groupItems]) => {
                if (cat === '') return groupItems.map(renderRow)
                // Searching force-expands every group so hits aren't hidden.
                const open = searching || openCat === cat
                return (
                  <div key={cat} className={`ad-list-group${open ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setOpenCat(open && !searching ? null : cat)}
                      className="ad-list-group-head"
                      aria-expanded={open}
                    >
                      <span className="ad-list-group-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                      <span className="ad-list-group-label">{cat}</span>
                      <span className="ad-list-group-count">{groupItems.length}</span>
                    </button>
                    {open && groupItems.map(renderRow)}
                  </div>
                )
              })}
          {pinned.map(it => (
            <button
              type="button"
              key={it.key}
              onClick={() => onSelect(it.key)}
              className={`ad-list-row ad-list-row--sparket${it.key === selectedKey ? ' is-active' : ''}`}
            >
              <span className="ad-list-name">{it.label}</span>
              {it.right}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
